import React, { useState, useEffect } from 'react';
import { CATEGORIES } from '../constants';
import { autoDetectCategory } from '../services/gemini';
import Header from './Header';

const AdminAddProduct: React.FC = () => {
  // Use lazy state initialization to retrieve the saved secret from localStorage
  const [adminSecret, setAdminSecret] = useState<string>(() => {
    return localStorage.getItem('qoc_admin_secret') || '';
  });
  
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('');
  const [customName, setCustomName] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successProduct, setSuccessProduct] = useState<any | null>(null);

  // Auto-detection states
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  const [detectedTitle, setDetectedTitle] = useState('');

  // Search Amazon states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [addingAsin, setAddingAsin] = useState<string | null>(null);

  // Products list states
  const [products, setProducts] = useState<any[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);
  
  // Deletion states
  const [deletingAsin, setDeletingAsin] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter out 'All' category for product selection
  const validCategories = CATEGORIES.filter(c => c !== 'All');

  // Category management states
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);

  const dynamicCategories = categories.length > 0 ? categories.map(c => c.name) : validCategories;

  // Identify products needing attention
  const attentionProducts = products.filter(p => {
    const hasZeroPrice = p.price === 0 || p.price === null || p.price === undefined;
    const hasNoImage = !p.image || p.image.trim() === '';
    const hasNoName = !p.name || p.name.trim() === '';
    return hasZeroPrice || hasNoImage || hasNoName;
  });

  // Collapsible sections state
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);
  const [isAddProductExpanded, setIsAddProductExpanded] = useState(false);

  // Encouragement widget state
  const [encouragement, setEncouragement] = useState<string | null>(null);
  const [isEncouragementLoading, setIsEncouragementLoading] = useState(false);
  const [encouragementError, setEncouragementError] = useState<string | null>(null);

  // Business Ideas state
  const [businessIdeas, setBusinessIdeas] = useState<string[]>([]);
  const [isBusinessIdeasLoading, setIsBusinessIdeasLoading] = useState(false);
  const [businessIdeasError, setBusinessIdeasError] = useState<string | null>(null);
  const [isIdeaGeneratorExpanded, setIsIdeaGeneratorExpanded] = useState(false);

  // Click statistics state
  const [clickStats, setClickStats] = useState<Record<string, number>>({});
  const [isClickStatsLoading, setIsClickStatsLoading] = useState(false);

  // Memoized top clicked products
  const topClickedProducts = React.useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return products
      .map((p) => ({
        ...p,
        clicks: clickStats[p.asin || p.id] || 0
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);
  }, [products, clickStats]);

  const fetchClickStats = async () => {
    if (!adminSecret) return;
    setIsClickStatsLoading(true);
    try {
      const response = await fetch('/api/admin/click-stats', {
        headers: {
          'x-admin-secret': adminSecret
        }
      });
      const data = await response.json();
      if (response.ok) {
        setClickStats(data);
      }
    } catch (err) {
      console.error('Failed to load click stats', err);
    } finally {
      setIsClickStatsLoading(false);
    }
  };

  // Filter by category state for Existing Products
  const [selectedFilterCategory, setSelectedFilterCategory] = useState('All Categories');

  const usedCategories = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort() as string[];

  const filteredProducts = selectedFilterCategory === 'All Categories'
    ? products
    : products.filter(p => p.category === selectedFilterCategory);

  // Share product states
  const [selectedShareProduct, setSelectedShareProduct] = useState<any | null>(null);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [imageCopied, setImageCopied] = useState(false);

  const handleCopyCaption = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy caption', err);
    }
  };

  const handleCopyImageLink = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setImageCopied(true);
      setTimeout(() => setImageCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy image link', err);
    }
  };

  const handleNativeShare = async (product: any) => {
    if (typeof navigator === 'undefined' || !navigator.share) return;
    const caption = `${product.name}\n\n${product.description || ''}\n\nShop here: ${product.url}`;
    try {
      await navigator.share({
        title: product.name,
        text: caption,
        url: product.url,
      });
    } catch (err) {
      console.error('Error sharing product', err);
    }
  };

  // Friendly error mapping to make messages plain English and supportive
  const getFriendlyError = (errMessage: string): string => {
    if (!errMessage) return "Something went wrong. Please check your connection and try again.";
    const msg = errMessage.toLowerCase();
    if (msg.includes("asin") || msg.includes("extract")) {
      return "That doesn't look like an Amazon product link — try copying it again from the Amazon app or website.";
    }
    if (msg.includes("unauthorized") || msg.includes("secret")) {
      return "The Admin Secret password is incorrect. Please double check and try again.";
    }
    return errMessage;
  };

  const fetchProducts = async () => {
    if (!adminSecret) {
      setProductsError('Please enter the Admin Secret password first.');
      return;
    }
    setIsProductsLoading(true);
    setProductsError(null);
    try {
      const response = await fetch('/api/admin/products', {
        headers: {
          'x-admin-secret': adminSecret
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch products');
      }
      setProducts(data);
      setHasLoadedInitially(true);
      // Save secret to localStorage once authenticated successfully
      localStorage.setItem('qoc_admin_secret', adminSecret);
    } catch (err: any) {
      setProductsError(getFriendlyError(err.message || 'Failed to load products'));
    } finally {
      setIsProductsLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!adminSecret) return;
    setIsCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const response = await fetch('/api/admin/categories', {
        headers: {
          'x-admin-secret': adminSecret
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch categories');
      }
      setCategories(data);
    } catch (err: any) {
      setCategoriesError(err.message || 'Failed to load categories');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const fetchEncouragement = async () => {
    if (!adminSecret) return;
    setIsEncouragementLoading(true);
    setEncouragementError(null);
    try {
      const response = await fetch('/api/admin/encouragement', {
        headers: {
          'x-admin-secret': adminSecret
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch encouragement');
      }
      setEncouragement(data.message || '');
    } catch (err: any) {
      setEncouragementError(err.message || 'Failed to load encouragement');
    } finally {
      setIsEncouragementLoading(false);
    }
  };

  const fetchBusinessIdeas = async () => {
    if (!adminSecret) return;
    setIsBusinessIdeasLoading(true);
    setBusinessIdeasError(null);
    try {
      const response = await fetch('/api/admin/business-ideas', {
        headers: {
          'x-admin-secret': adminSecret
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch business ideas');
      }
      setBusinessIdeas(data.ideas || []);
    } catch (err: any) {
      setBusinessIdeasError(err.message || 'Failed to load business ideas');
      // If we got an error but it returned some static ideas (fallback in error response is array of strings), use them, otherwise use fallback array directly
      setBusinessIdeas([
        "Launch a seasonal 'Organize My Home' digital guidebook or printable planner pack specifically designed for UK cleaning routines.",
        "Create co-branded eco-friendly cleaning cloths or reusable sponge sets with a local UK brand to bundle with your existing spray line.",
        "Introduce a members-only monthly 'Home Haven' newsletter or subscription group offering exclusive cleaning hacks, live Q&As, and partner discounts."
      ]);
    } finally {
      setIsBusinessIdeasLoading(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminSecret) return;
    if (!newCategoryName.trim()) return;

    setCategoriesError(null);
    setIsCategoriesLoading(true);

    try {
      const response = await fetch('/api/admin/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-secret': adminSecret
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add category');
      }

      setNewCategoryName('');
      setCategories(prev => [...prev, data]);
    } catch (err: any) {
      setCategoriesError(err.message || 'An unexpected error occurred while adding category');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!adminSecret) return;
    setIsDeletingCategory(true);
    setCategoriesError(null);

    try {
      const response = await fetch(`/api/admin/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'x-admin-secret': adminSecret
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete category');
      }

      setCategories(prev => prev.filter(c => c.id !== id));
      setDeletingCategoryId(null);
    } catch (err: any) {
      setCategoriesError(err.message || 'Failed to delete category');
    } finally {
      setIsDeletingCategory(false);
    }
  };

  // Automatically fetch products and categories if adminSecret is filled
  useEffect(() => {
    if (adminSecret && adminSecret.trim().length >= 4) {
      const timer = setTimeout(() => {
        fetchProducts();
        fetchCategories();
        fetchEncouragement();
        fetchBusinessIdeas();
        fetchClickStats();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [adminSecret]);

  // Clean helper to extract ASIN client-side for auto-detection
  const extractAsin = (targetUrl: string): string | null => {
    const dpMatch = targetUrl.match(/\/dp\/([A-Za-z0-9]{10})(?:[\/?#]|$)/i);
    if (dpMatch) return dpMatch[1].toUpperCase();

    const gpMatch = targetUrl.match(/\/gp\/product\/([A-Za-z0-9]{10})(?:[\/?#]|$)/i);
    if (gpMatch) return gpMatch[1].toUpperCase();

    return null;
  };

  // Auto-detect title and pre-select category when URL matches a product
  useEffect(() => {
    const asin = extractAsin(url);
    if (asin) {
      const detectDetails = async () => {
        setIsAutoDetecting(true);
        setError(null);
        try {
          const response = await fetch('/api/amazon/items', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ asins: [asin] })
          });
          const items = await response.json();
          if (response.ok && items?.[0]) {
            const title = items[0].title;
            if (title) {
              setDetectedTitle(title);
              // Use Gemini to auto-detect category based on title
              const detectedCat = await autoDetectCategory(title);
              if (detectedCat) {
                setCategory(detectedCat);
              }
            }
          }
        } catch (err) {
          console.warn("Details auto-detection failed", err);
        } finally {
          setIsAutoDetecting(false);
        }
      };
      detectDetails();
    } else {
      setDetectedTitle('');
    }
  }, [url]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearchError(null);
    setSearchResults([]);

    if (!adminSecret) {
      setSearchError('Please enter the Admin Secret password first.');
      return;
    }
    if (!searchQuery.trim()) {
      setSearchError('Search query is required.');
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch('/api/admin/search-amazon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-secret': adminSecret
        },
        body: JSON.stringify({ query: searchQuery })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to search Amazon');
      }
      setSearchResults(data);
      if (data.length === 0) {
        setSearchError('No products found on Amazon matching that query.');
      }
    } catch (err: any) {
      setSearchError(err.message || 'An unexpected error occurred during search.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddSearchResult = async (result: any) => {
    setAddingAsin(result.asin);
    setError(null);
    setSuccessProduct(null);

    if (!adminSecret) {
      setError('Admin Secret is required.');
      setAddingAsin(null);
      return;
    }

    try {
      // 1. Auto-detect category
      let detectedCat = await autoDetectCategory(result.title);
      if (!detectedCat || !dynamicCategories.includes(detectedCat)) {
        detectedCat = dynamicCategories.includes('Lynsey Favorites') ? 'Lynsey Favorites' : (dynamicCategories[0] || ''); // default fallback
      }

      // 2. Call existing add-product endpoint with the ASIN-based URL and detected category
      const targetUrl = `https://www.amazon.co.uk/dp/${result.asin}`;
      const response = await fetch('/api/admin/add-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-secret': adminSecret
        },
        body: JSON.stringify({
          url: targetUrl,
          category: detectedCat
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add product');
      }

      setSuccessProduct(data);
      
      // Auto-update products list
      setProducts(prev => {
        const filtered = prev.filter(p => p.asin !== data.asin);
        return [data, ...filtered];
      });
      setHasLoadedInitially(true);
    } catch (err: any) {
      setError(getFriendlyError(err.message || 'An unexpected error occurred'));
    } finally {
      setAddingAsin(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessProduct(null);

    if (!adminSecret) {
      setError('Admin Secret is required.');
      return;
    }
    if (!url) {
      setError('Amazon URL is required.');
      return;
    }
    if (!category) {
      setError('Please select a category.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/admin/add-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-secret': adminSecret
        },
        body: JSON.stringify({
          url,
          category,
          name: customName || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add product');
      }

      setSuccessProduct(data);
      setUrl('');
      setCustomName('');
      setDetectedTitle('');
      
      // Save secret to localStorage on success
      localStorage.setItem('qoc_admin_secret', adminSecret);
      
      // Auto-update products list if loaded
      setProducts(prev => {
        const filtered = prev.filter(p => p.asin !== data.asin);
        return [data, ...filtered];
      });
      setHasLoadedInitially(true);
    } catch (err: any) {
      setError(getFriendlyError(err.message || 'An unexpected error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (asin: string) => {
    if (!adminSecret) return;
    setIsDeleting(true);
    setProductsError(null);
    try {
      const response = await fetch(`/api/admin/products/${asin}`, {
        method: 'DELETE',
        headers: {
          'x-admin-secret': adminSecret
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete product');
      }
      setProducts(prev => prev.filter(p => p.asin !== asin));
      setDeletingAsin(null);
    } catch (err: any) {
      setProductsError(getFriendlyError(err.message || 'Failed to delete product'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('qoc_admin_secret');
    setAdminSecret('');
    setProducts([]);
    setHasLoadedInitially(false);
    setError(null);
    setProductsError(null);
    setSuccessProduct(null);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
  };

  const QOC_LOGO = "https://firebasestorage.googleapis.com/v0/b/gen-lang-client-0034302309.firebasestorage.app/o/QOC%20Logo.webp?alt=media&token=6cee1cd4-c0d6-4499-9413-4d7f5dc69021";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans flex flex-col items-center w-full relative overflow-x-hidden">
      <Header logoUrl={QOC_LOGO} />
      <div className="max-w-7xl w-full p-4 sm:p-6 lg:p-8 py-12">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8 mb-8 animate-fadeIn w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600 shadow-sm shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">Admin Product Panel</h1>
                <p className="text-xs sm:text-sm text-slate-500">Add new Amazon products directly to your storefront database.</p>
              </div>
            </div>
            {adminSecret && (
              <button 
                type="button" 
                onClick={handleLogout}
                className="text-xs text-red-500 hover:text-red-700 font-bold transition-colors bg-red-50 hover:bg-red-100/80 px-3.5 py-2 rounded-xl border border-red-100 self-start sm:self-center cursor-pointer min-h-[38px]"
              >
                Log Out
              </button>
            )}
          </div>
        </div>

        {/* Password input block if not authenticated */}
        {!adminSecret && (
          <div className="max-w-md mx-auto w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8 md:p-10 animate-fadeIn">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Admin Secret Password</label>
            <input 
              type="password"
              value={adminSecret}
              onChange={(e) => setAdminSecret(e.target.value)}
              placeholder="Enter server ADMIN_SECRET"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm h-12"
              required
            />
          </div>
        )}

        {/* Authenticated Admin Actions */}
        {adminSecret && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start w-full">
            {/* Sidebar Column */}
            <div className="lg:col-span-4 lg:sticky lg:top-8 space-y-6 w-full order-2 lg:order-2">
              {/* Encouragement Widget */}
              <div className="bg-gradient-to-r from-[#FFF5F6] to-[#FFF9FA] border border-[#F1B1BA]/30 rounded-3xl p-5 md:p-6 mb-8 shadow-sm flex items-start gap-4 animate-fadeIn relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#F1B1BA]/5 rounded-full blur-xl pointer-events-none"></div>
                <div className="w-10 h-10 rounded-full bg-[#F1B1BA]/10 flex items-center justify-center shrink-0 text-xl">
                  🌸
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-[10px] font-black text-[#F1B1BA] uppercase tracking-[0.2em] mb-1">
                    A Note of Encouragement
                  </span>
                  {isEncouragementLoading && !encouragement ? (
                    <div className="h-5 bg-pink-100/40 rounded w-3/4 animate-pulse mt-1"></div>
                  ) : encouragementError ? (
                    <p className="text-slate-500 text-xs italic">
                      "You are doing an amazing job curating these beautiful products, Lynsey! Every selection shows your expert touch and care."
                    </p>
                  ) : (
                    <p className="text-slate-700 text-sm sm:text-base leading-relaxed italic font-medium">
                      "{encouragement || "You are doing an amazing job curating these beautiful products, Lynsey! Every selection shows your expert touch and care."}"
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={fetchEncouragement}
                  disabled={isEncouragementLoading}
                  className="p-2.5 bg-white hover:bg-[#F1B1BA]/10 text-[#5C3E43]/70 hover:text-[#5C3E43] rounded-xl border border-pink-100/80 shadow-sm transition-all flex items-center justify-center cursor-pointer shrink-0 disabled:opacity-50 self-center"
                  title="Get a new encouraging message"
                >
                  <i className={`fas fa-sync-alt ${isEncouragementLoading ? 'animate-spin' : ''} text-xs`}></i>
                </button>
              </div>
            </div>

            {/* Main Column */}
            <div className="lg:col-span-8 space-y-8 w-full order-1 lg:order-1">
            {/* Needs Your Attention Banner */}
            {attentionProducts.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-3xl p-5 mb-8 animate-fadeIn flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <span className="text-xl shrink-0 mt-0.5" role="img" aria-label="warning">⚠️</span>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm sm:text-base text-amber-950">
                      Heads up — {attentionProducts.length} {attentionProducts.length === 1 ? 'product needs' : 'products need'} a quick look:
                    </h3>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {attentionProducts.map((p) => {
                        const name = p.name || `Unnamed Product (${p.asin})`;
                        return (
                          <button
                            key={p.asin}
                            type="button"
                            onClick={() => {
                              document.getElementById(`product-${p.asin}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold text-xs rounded-xl border border-amber-200 transition-all cursor-pointer"
                          >
                            <span>{name}</span>
                            <span className="text-[10px] text-amber-600 font-medium">
                              ({p.price === 0 || p.price === null || p.price === undefined ? 'no price' : !p.image ? 'no image' : 'no name'})
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-8 animate-fadeIn">
            
              {/* 1. Manage Categories Section */}
              <div className="bg-white rounded-3xl shadow-xl border border-slate-100 px-6 py-5 sm:px-8 w-full transition-all duration-300">
                <button
                  type="button"
                  onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)}
                  className={`w-full text-left flex items-center justify-between cursor-pointer group ${isCategoriesExpanded ? 'pb-3 border-b border-slate-100 mb-5' : ''}`}
                >
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-slate-900 flex items-center gap-2">
                    <span className="w-2.5 h-6 bg-[#F1B1BA] rounded-full inline-block"></span>
                    Manage Categories
                  </h2>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-transform duration-300 ${isCategoriesExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isCategoriesExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="overflow-hidden">
                    {categoriesError && (
                      <div className="mb-4 p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-xs flex items-start gap-2.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{categoriesError}</span>
                      </div>
                    )}

                    {/* Add Category Form */}
                    <form onSubmit={handleAddCategory} className="mb-6 flex gap-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="New category name..."
                        className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm h-11"
                        required
                      />
                      <button
                        type="submit"
                        disabled={isCategoriesLoading}
                        className="px-4 py-2.5 bg-[#5C3E43] hover:bg-[#4a3236] disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all cursor-pointer h-11 flex items-center justify-center shrink-0"
                      >
                        Add
                      </button>
                    </form>

                    {/* Categories List */}
                    {isCategoriesLoading && categories.length === 0 ? (
                      <div className="text-center py-4">
                        <div className="inline-block w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : categories.length === 0 ? (
                      <p className="text-xs text-slate-400 font-medium text-center py-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        No custom categories added yet. Falling back to default list.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {categories.map((cat) => (
                          <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200/50 hover:border-slate-300 transition-all">
                            <span className="text-sm font-bold text-slate-800">{cat.name}</span>
                            
                            {deletingCategoryId === cat.id ? (
                              <div className="flex items-center gap-1.5 animate-fadeIn">
                                <span className="text-red-600 font-bold text-[10px]">Sure?</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCategory(cat.id)}
                                  disabled={isDeletingCategory}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-200 text-white font-bold text-[10px] rounded-lg transition-colors cursor-pointer"
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletingCategoryId(null)}
                                  disabled={isDeletingCategory}
                                  className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[10px] rounded-lg transition-colors cursor-pointer"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setDeletingCategoryId(cat.id)}
                                className="text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg transition-all cursor-pointer"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Performance Tracker: Most Clicked (Last 30d) Horizontal Section */}
              <div className="bg-gradient-to-r from-[#FFFDF9] to-[#FFFBF5] border border-amber-200/40 rounded-3xl p-5 md:p-6 shadow-sm flex flex-col gap-4 animate-fadeIn relative overflow-hidden w-full">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none"></div>
                
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center text-xl shrink-0">
                      🔥
                    </div>
                    <div>
                      <span className="block text-[10px] font-black text-amber-600 uppercase tracking-[0.2em] mb-0.5">
                        Performance Tracker
                      </span>
                      <h3 className="text-sm sm:text-base font-black text-slate-800 leading-none">
                        Most Clicked (Last 30d)
                      </h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={fetchClickStats}
                    disabled={isClickStatsLoading}
                    className="p-1.5 px-3 bg-white hover:bg-amber-500/5 text-amber-600 rounded-xl border border-amber-100/60 shadow-xs transition-all flex items-center justify-center gap-1.5 font-bold text-xs cursor-pointer disabled:opacity-50 self-start sm:self-auto"
                    title="Refresh stats"
                  >
                    <i className={`fas fa-sync-alt ${isClickStatsLoading ? 'animate-spin' : ''} text-[10px]`}></i>
                    <span>Refresh Stats</span>
                  </button>
                </div>

                {isClickStatsLoading && topClickedProducts.length === 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
                    <div className="h-16 bg-amber-100/30 rounded-2xl"></div>
                    <div className="h-16 bg-amber-100/30 rounded-2xl"></div>
                    <div className="h-16 bg-amber-100/30 rounded-2xl"></div>
                    <div className="h-16 bg-amber-100/30 rounded-2xl"></div>
                    <div className="h-16 bg-amber-100/30 rounded-2xl"></div>
                  </div>
                ) : topClickedProducts.length === 0 ? (
                  <p className="text-slate-400 text-xs italic text-center py-4 bg-white/50 border border-slate-100 rounded-2xl">
                    No tracking data available yet.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {topClickedProducts.map((p, idx) => (
                      <div key={p.asin || p.id || idx} className="bg-white/85 p-3 rounded-2xl border border-slate-100 shadow-xs hover:border-amber-200 transition-all flex items-center gap-3 relative min-w-0">
                        <div className="absolute top-1 right-1.5 w-5 h-5 rounded-full bg-amber-50 flex items-center justify-center text-[9px] font-black text-amber-700 border border-amber-100/60">
                          #{idx + 1}
                        </div>
                        
                        {p.image ? (
                          <img 
                            src={p.image} 
                            alt={p.name} 
                            className="w-10 h-10 object-contain rounded-lg border border-slate-50 p-0.5 bg-white shrink-0"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-[9px] font-bold text-slate-400 border border-slate-100 shrink-0">
                            N/A
                          </div>
                        )}

                        <div className="min-w-0 flex-1 pr-3">
                          <p className="font-bold text-xs text-slate-800 truncate leading-snug" title={p.name}>
                            {p.name}
                          </p>
                          <p className="font-semibold text-[9px] text-slate-400 mt-0.5 uppercase tracking-wider truncate">
                            {p.category}
                          </p>
                          <div className="mt-1 flex items-center gap-1.5">
                            <span className={`inline-flex items-center text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                              p.clicks > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {p.clicks} click{p.clicks === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 2. Add Product Section */}
              <div className="bg-white rounded-3xl shadow-xl border border-slate-100 px-6 py-5 sm:px-8 w-full transition-all duration-300">
                <button
                  type="button"
                  onClick={() => setIsAddProductExpanded(!isAddProductExpanded)}
                  className={`w-full text-left flex items-center justify-between cursor-pointer group ${isAddProductExpanded ? 'pb-3 border-b border-slate-100 mb-5' : ''}`}
                >
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-slate-900 flex items-center gap-2">
                    <span className="w-2.5 h-6 bg-[#F1B1BA] rounded-full inline-block"></span>
                    Add Product
                  </h2>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-transform duration-300 ${isAddProductExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isAddProductExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="overflow-hidden">
                    {/* Global Action Messages */}
                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-start gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{error}</span>
                      </div>
                    )}

                    {successProduct && (
                      <div className="mb-6 p-5 bg-green-50 border border-green-200 text-green-950 rounded-2xl">
                        <div className="flex items-center gap-2 mb-3 text-green-700 font-bold text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Added! {successProduct.name} is now live on your store.</span>
                        </div>
                        <div className="flex gap-4 items-start bg-white p-3 rounded-xl border border-green-100/80">
                          {successProduct.image && (
                            <img 
                              src={successProduct.image} 
                              alt={successProduct.name} 
                              className="w-14 h-14 object-contain rounded-lg border bg-slate-50 p-1 shrink-0"
                              referrerPolicy="no-referrer"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-xs sm:text-sm text-slate-900 truncate">{successProduct.name}</h4>
                            <div className="flex items-center gap-3 mt-1.5 text-xs">
                              <span className="font-bold text-slate-900">£{successProduct.price.toFixed(2)}</span>
                              <span className="bg-pink-50 text-pink-700 font-bold px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">{successProduct.category}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                      {/* Left Side: Quick Search Amazon */}
                      <div className="bg-slate-50/50 rounded-2xl p-4 sm:p-5 border border-slate-200/50 flex flex-col h-full justify-between">
                        <div>
                          <h3 className="text-xs sm:text-sm font-black uppercase tracking-wider text-slate-900 mb-3 flex items-center gap-1.5">
                            <span>🔍</span> Quick Search Amazon
                          </h3>
                          <form onSubmit={handleSearch} className="flex flex-col gap-2.5">
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Type keyword e.g. scrub daddy, spray bottle..."
                              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm h-12"
                            />
                            <button
                              type="submit"
                              disabled={isSearching}
                              className="w-full py-3 bg-[#5C3E43] hover:bg-[#4a3236] disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-xs sm:text-sm uppercase tracking-wider rounded-2xl transition-all cursor-pointer h-12 flex items-center justify-center"
                            >
                              {isSearching ? 'Searching' : 'Search'}
                            </button>
                          </form>
                          <p className="text-[11px] text-slate-400 font-medium mt-2">
                            Tip: be specific for the best results, e.g. 'scrub daddy sponge'
                          </p>

                          {searchError && (
                            <p className="text-xs text-red-600 mt-2 font-bold flex items-center gap-1">
                              <span>⚠️</span> {searchError}
                            </p>
                          )}

                          {/* Search Results Grid - 3-4 columns on larger areas */}
                          {searchResults.length > 0 && (
                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[380px] overflow-y-auto pr-1">
                              {searchResults.map((result) => (
                                <div key={result.asin} className="bg-white p-3 rounded-xl border border-slate-200/60 flex flex-col justify-between shadow-sm hover:border-slate-300 transition-all">
                                  <div className="flex items-center gap-2.5 mb-2">
                                    <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-lg p-1 shrink-0 flex items-center justify-center">
                                      {result.image ? (
                                        <img src={result.image} alt={result.title} className="max-w-full max-h-full object-contain" referrerPolicy="no-referrer" />
                                      ) : (
                                        <span className="text-[8px] text-slate-400 font-bold">No Image</span>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <h4 className="text-[11px] font-bold text-slate-900 truncate" title={result.title}>{result.title}</h4>
                                      <p className="text-[10px] font-black text-slate-900">£{result.price ? result.price.toFixed(2) : '0.00'}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    disabled={addingAsin === result.asin}
                                    onClick={() => handleAddSearchResult(result)}
                                    className="w-full py-1.5 bg-[#F1B1BA] hover:bg-[#eb9fa9] disabled:bg-slate-100 disabled:text-slate-400 text-[#5C3E43] font-black text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer min-h-[28px]"
                                  >
                                    {addingAsin === result.asin ? (
                                      <>
                                        <span className="w-2.5 h-2.5 border-2 border-pink-700 border-t-transparent rounded-full animate-spin"></span>
                                        <span>Adding</span>
                                      </>
                                    ) : (
                                      'Add to Store'
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Side: Paste a Link */}
                      <div className="bg-slate-50/50 rounded-2xl p-4 sm:p-5 border border-slate-200/50 h-full flex flex-col justify-between">
                        <div>
                          <h3 className="text-xs sm:text-sm font-black uppercase tracking-wider text-slate-900 mb-3 flex items-center gap-1.5">
                            <span>🔗</span> Paste-a-Link
                          </h3>
                          <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Paste the Amazon link here</label>
                              <input 
                                type="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://www.amazon.co.uk/dp/B0027ICKCY..."
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm h-12"
                                required={!searchQuery}
                              />
                              
                              {isAutoDetecting && (
                                <div className="mt-2 flex items-center gap-2 text-xs text-pink-600 font-semibold animate-pulse">
                                  <span className="inline-block w-3.5 h-3.5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></span>
                                  <span>✨ Auto-detecting category...</span>
                                </div>
                              )}

                              {!isAutoDetecting && detectedTitle && (
                                <div className="mt-2 text-xs text-slate-600 bg-pink-50/40 border border-pink-100/60 p-3 rounded-xl flex gap-2 items-start animate-fadeIn">
                                  <span className="text-pink-500 shrink-0 mt-0.5">✨</span>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-slate-500">Detected product:</p>
                                    <p className="font-bold text-slate-800 truncate mt-0.5" title={detectedTitle}>{detectedTitle}</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-4">
                              <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Category</label>
                                <div className="relative">
                                  <select 
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm appearance-none cursor-pointer h-12 pr-10"
                                    required
                                  >
                                    <option value="">Select category...</option>
                                    {dynamicCategories.map((cat) => (
                                      <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                  </select>
                                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Custom Name (Optional)</label>
                                <input 
                                  type="text"
                                  value={customName}
                                  onChange={(e) => setCustomName(e.target.value)}
                                  placeholder="Custom display title on your store"
                                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm h-12"
                                />
                              </div>
                            </div>

                            <button
                              type="submit"
                              disabled={isLoading || isAutoDetecting}
                              className="w-full min-h-[48px] py-3 bg-[#F1B1BA] hover:bg-[#eb9fa9] disabled:bg-slate-100 disabled:text-slate-400 text-[#5C3E43] font-black uppercase tracking-wider text-xs sm:text-sm rounded-2xl transition-all shadow-md shadow-pink-900/10 active:scale-[0.98] flex items-center justify-center cursor-pointer"
                            >
                              {isLoading ? 'Adding to store database...' : 'Add This to My Store'}
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Manage Existing Products Section */}
              <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8 flex flex-col w-full">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100 shrink-0">
                  <div>
                    <h2 className="text-lg sm:text-xl font-black tracking-tight text-slate-900">Manage Existing Products</h2>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1">View, inspect, and remove products currently in the database.</p>
                  </div>
                  <button
                    onClick={fetchProducts}
                    disabled={isProductsLoading || !adminSecret}
                    className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400 text-slate-700 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer min-h-[40px] shrink-0"
                  >
                    {isProductsLoading ? 'Loading...' : 'Refresh List'}
                  </button>
                </div>

                {productsError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-start gap-3 shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{productsError}</span>
                  </div>
                )}

                {isProductsLoading && products.length === 0 && (
                  <div className="text-center py-12 flex-1 flex flex-col items-center justify-center">
                    <div className="inline-block w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-xs sm:text-sm text-slate-500 mt-3 font-medium">Fetching product list...</p>
                  </div>
                )}

                {!isProductsLoading && hasLoadedInitially && products.length === 0 && (
                  <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-4 flex-1 flex flex-col items-center justify-center">
                    <p className="text-xs sm:text-sm font-semibold text-slate-500">No custom Amazon products found in the database yet.</p>
                  </div>
                )}

                {products.length > 0 && (
                  <div className="w-full">
                    {/* Stats Summary Bar */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 shrink-0">
                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 text-center">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Total</span>
                        <span className="text-lg font-black text-slate-900">{products.length}</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 text-center">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">In Stock</span>
                        <span className="text-lg font-black text-slate-900">{products.filter(p => p.inStock !== false).length}</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 text-center">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Out of Stock</span>
                        <span className="text-lg font-black text-slate-900">{products.filter(p => p.inStock === false).length}</span>
                      </div>
                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 text-center">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Categories</span>
                        <span className="text-lg font-black text-slate-900">{usedCategories.length}</span>
                      </div>
                    </div>

                    {/* Category Filter Dropdown */}
                    <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50 p-4 rounded-2xl border border-slate-200/50 shrink-0">
                      <div className="flex items-center gap-3">
                        <label htmlFor="category-filter" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          Filter by category:
                        </label>
                        <div className="relative min-w-[200px]">
                          <select
                            id="category-filter"
                            value={selectedFilterCategory}
                            onChange={(e) => setSelectedFilterCategory(e.target.value)}
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-xs font-semibold appearance-none cursor-pointer pr-10"
                          >
                            <option value="All Categories">All Categories</option>
                            {usedCategories.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-slate-500 bg-white border border-slate-200 px-3 py-1.5 rounded-xl">
                        {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                      </span>
                    </div>

                    {filteredProducts.length > 0 ? (
                      /* Responsive Grid for products - 2 to 3 per row on larger screens */
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {filteredProducts.map((product) => (
                          <div key={product.asin} id={`product-${product.asin}`} className="group bg-white rounded-2xl border border-slate-200/80 p-4 flex flex-col justify-between hover:shadow-md hover:border-slate-300 transition-all animate-fadeIn">
                            <div>
                              <div className="relative aspect-square w-full bg-slate-50 border border-slate-100 rounded-xl p-2 mb-3 flex items-center justify-center overflow-hidden shrink-0">
                                {product.image ? (
                                  <img 
                                    src={product.image} 
                                    alt={product.name} 
                                    className="max-w-full max-h-full object-contain rounded group-hover:scale-105 transition-transform duration-300"
                                    referrerPolicy="no-referrer"
                                  />
                                ) : (
                                  <span className="text-xs text-slate-400 font-bold">No Image</span>
                                )}
                                <span className="absolute top-2 left-2 bg-slate-900/85 backdrop-blur-xs text-white font-bold text-[9px] px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                                  {product.category}
                                </span>
                              </div>
                              
                              <h4 className="font-bold text-xs sm:text-sm text-slate-950 line-clamp-2 min-h-[32px] mb-1" title={product.name}>
                                {product.name}
                              </h4>
                              <p className="font-mono text-[9px] text-slate-400 select-all tracking-wider mb-2">
                                ASIN: {product.asin}
                              </p>
                              {/* Individual Product Click Statistics Badge */}
                              <div className="mt-2 mb-3">
                                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                                  (clickStats[product.asin || product.id] || 0) > 0
                                    ? 'bg-pink-50 text-[#5C3E43] border-[#F1B1BA]/30'
                                    : 'bg-slate-50 text-slate-400 border-slate-200'
                                }`}>
                                  <span>🖱️</span>
                                  <span>
                                    {(clickStats[product.asin || product.id] || 0) > 0 
                                      ? `${clickStats[product.asin || product.id]} click${clickStats[product.asin || product.id] === 1 ? '' : 's'} (30d)`
                                      : 'No clicks yet'
                                    }
                                  </span>
                                </span>
                              </div>
                            </div>

                            <div className="pt-3 border-t border-slate-100 mt-2 flex items-center justify-between gap-2 shrink-0">
                              <span className="font-black text-sm text-slate-900">£{product.price ? product.price.toFixed(2) : '0.00'}</span>
                              <div className="flex items-center gap-1.5">
                                <a 
                                  href={product.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="px-2.5 py-1.5 bg-pink-50 hover:bg-pink-100 text-pink-700 font-bold text-[11px] rounded-xl transition-all"
                                >
                                  View
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setSelectedShareProduct(product)}
                                  className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] rounded-xl transition-all cursor-pointer"
                                >
                                  Share
                                </button>
                                {deletingAsin === product.asin ? (
                                  <div className="flex items-center gap-1 bg-red-50 border border-red-100 rounded-xl p-1 animate-fadeIn">
                                    <span className="text-red-600 font-bold text-[10px] px-1">Sure?</span>
                                    <button
                                      onClick={() => handleDelete(product.asin)}
                                      disabled={isDeleting}
                                      className="px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-200 text-white font-bold text-[10px] rounded-lg transition-colors cursor-pointer"
                                    >
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => setDeletingAsin(null)}
                                      disabled={isDeleting}
                                      className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[10px] rounded-lg transition-colors cursor-pointer"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setDeletingAsin(product.asin)}
                                    className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold text-[11px] rounded-xl transition-all cursor-pointer"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-4">
                        <p className="text-xs sm:text-sm font-semibold text-slate-500">No products match the selected category.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

        {/* Idea Generator Widget as Floating Bubble & Popup */}
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
          {/* Popup card */}
          {isIdeaGeneratorExpanded && (
            <div className="bg-gradient-to-r from-[#FAFAFA] to-[#F5F5F5] border border-slate-200 shadow-2xl rounded-3xl p-5 md:p-6 mb-4 w-[350px] max-w-[calc(100vw-2rem)] animate-fadeIn relative overflow-hidden max-h-[450px] overflow-y-auto">
              <div className="absolute top-0 right-0 w-24 h-24 bg-slate-300/10 rounded-full blur-xl pointer-events-none"></div>
              
              {/* Close button inside the card */}
              <button
                type="button"
                onClick={() => setIsIdeaGeneratorExpanded(false)}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-200/50 transition-colors cursor-pointer"
                title="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-xl border border-slate-200/40">
                  💡
                </div>
                <div className="flex-1 min-w-0 pr-4">
                  <span className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1 leading-snug">
                    Ideas to explore (AI-generated, worth discussing further before acting)
                  </span>
                </div>
              </div>

              {isBusinessIdeasLoading && businessIdeas.length === 0 ? (
                <div className="space-y-2 mt-3 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-full"></div>
                  <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                  <div className="h-4 bg-slate-200 rounded w-4/5"></div>
                </div>
              ) : (
                <ul className="space-y-2.5 mt-3">
                  {(businessIdeas.length > 0 ? businessIdeas : [
                    "Launch a seasonal 'Organize My Home' digital guidebook or printable planner pack specifically designed for UK cleaning routines.",
                    "Create co-branded eco-friendly cleaning cloths or reusable sponge sets with a local UK brand to bundle with your existing spray line.",
                    "Introduce a members-only monthly 'Home Haven' newsletter or subscription group offering exclusive cleaning hacks, live Q&As, and partner discounts."
                  ]).map((idea, index) => (
                    <li key={index} className="text-slate-700 text-xs sm:text-sm leading-relaxed flex items-start gap-2 bg-white/65 p-2.5 rounded-xl border border-slate-200/50 shadow-sm">
                      <span className="text-[#F1B1BA] font-black shrink-0 mt-0.5">•</span>
                      <span>{idea}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={fetchBusinessIdeas}
                  disabled={isBusinessIdeasLoading}
                  className="px-3.5 py-1.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-800 rounded-xl border border-slate-200/80 shadow-xs transition-all flex items-center justify-center gap-1.5 text-xs font-bold cursor-pointer disabled:opacity-50"
                  title="Refresh business ideas"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-3.5 w-3.5 ${isBusinessIdeasLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M21 20v-5h-.581" />
                  </svg>
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          )}

          {/* Floating Trigger Circle Icon */}
          <button
            type="button"
            onClick={() => setIsIdeaGeneratorExpanded(!isIdeaGeneratorExpanded)}
            className={`w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all cursor-pointer hover:scale-105 active:scale-95 text-white ${
              isIdeaGeneratorExpanded ? 'bg-rose-500' : 'bg-[#5C3E43]'
            }`}
            title={isIdeaGeneratorExpanded ? 'Close Idea Generator' : 'Open Idea Generator'}
          >
            {isIdeaGeneratorExpanded ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <span className="text-2xl select-none">💡</span>
            )}
          </button>
        </div>
      </div>
      )}
      </div>

      {/* Share Product Modal */}
      {selectedShareProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-[2rem] shadow-2xl border border-slate-100 max-w-lg w-full overflow-hidden animate-scaleIn flex flex-col">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-black text-[#5C3E43] uppercase tracking-wider text-sm flex items-center gap-2">
                <i className="fas fa-share-nodes text-[#F1B1BA]"></i>
                Share Product
              </h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedShareProduct(null);
                  setCaptionCopied(false);
                  setImageCopied(false);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-all cursor-pointer border-none"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5 overflow-y-auto max-h-[70vh]">
              {/* Product Card Preview inside Modal */}
              <div className="flex gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="w-20 h-20 bg-white border border-slate-200/60 rounded-xl p-1 flex items-center justify-center shrink-0 overflow-hidden">
                  <img
                    src={selectedShareProduct.image}
                    alt={selectedShareProduct.name}
                    className="max-w-full max-h-full object-contain"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-[9px] font-black uppercase tracking-wider text-pink-500 mb-1">
                    {selectedShareProduct.category}
                  </span>
                  <h4 className="font-bold text-xs sm:text-sm text-slate-900 line-clamp-2 leading-tight">
                    {selectedShareProduct.name}
                  </h4>
                  <p className="font-extrabold text-sm text-slate-950 mt-1">
                    £{selectedShareProduct.price ? selectedShareProduct.price.toFixed(2) : '0.00'}
                  </p>
                </div>
              </div>

              {/* Caption Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Pre-filled Caption
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopyCaption(`${selectedShareProduct.name}\n\n${selectedShareProduct.description || ''}\n\nShop here: ${selectedShareProduct.url}`)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-pink-600 hover:text-pink-700 transition-colors cursor-pointer"
                  >
                    <i className={captionCopied ? "fas fa-check text-green-500 animate-bounce" : "fas fa-copy"}></i>
                    <span>{captionCopied ? 'Copied!' : 'Copy Caption'}</span>
                  </button>
                </div>
                <div className="w-full h-32 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 overflow-y-auto select-all leading-relaxed whitespace-pre-wrap">
                  {`${selectedShareProduct.name}\n\n${selectedShareProduct.description || ''}\n\nShop here: ${selectedShareProduct.url}`}
                </div>
              </div>

              {/* Action Links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => handleCopyImageLink(selectedShareProduct.image)}
                  className="flex items-center justify-center gap-2 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer border-none"
                >
                  <i className={imageCopied ? "fas fa-check text-green-500" : "fas fa-image"}></i>
                  <span>{imageCopied ? 'Link Copied!' : 'Copy Image'}</span>
                </button>

                {typeof navigator !== 'undefined' && navigator.share && (
                  <button
                    type="button"
                    onClick={() => handleNativeShare(selectedShareProduct)}
                    className="flex items-center justify-center gap-2 py-3 bg-[#F1B1BA] hover:bg-[#eb9ea9] text-[#5C3E43] text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer border-none shadow-lg shadow-pink-900/10"
                  >
                    <i className="fas fa-arrow-up-from-bracket"></i>
                    <span>Share...</span>
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAddProduct;
